// LoginForm.tsx
import type { FormEvent } from 'react';
import { useState } from 'react';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import FloatingLabel from 'react-bootstrap/FloatingLabel';
import InputGroup from 'react-bootstrap/InputGroup';
import { Alert } from 'react-bootstrap';
import { NavLink } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';

type LogInValues = { username: string; password: string };

type LogInFormProps = {
  onSubmit?: (values: LogInValues) => Promise<void> | void;
  submitLabel?: string;
  errorMessage?: string;
};

export const LogInForm = ({ onSubmit, submitLabel = 'Login', errorMessage }: LogInFormProps) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [touched, setTouched] = useState({ username: false, password: false });
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Simple, reliable email check (good enough for inline UX)
  const emailIsValid = username.length > 0 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(username);

  const passwordIsValid = password.length > 0;
  const formIsValid = emailIsValid && passwordIsValid;

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setTouched({ username: true, password: true });
    if (!formIsValid) return;

    try {
      setSubmitting(true);
      await onSubmit?.({ username, password });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Form noValidate onSubmit={handleSubmit}>
      {errorMessage && <Alert variant="danger">{errorMessage}</Alert>}

      {/* Username / Email */}
      <Form.Group className="mb-3">
        <Form.Label>Username</Form.Label>

        <Form.Control
          type="email"
          placeholder="you@example.com"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          onBlur={() => setTouched((t) => ({ ...t, username: true }))}
          isValid={touched.username && emailIsValid}
          isInvalid={touched.username && !emailIsValid}
          autoComplete="username"
          required
        />
        <Form.Control.Feedback type="invalid">
          Please enter a valid email address.
        </Form.Control.Feedback>
      </Form.Group>

      {/* Password (with show/hide) */}
      <Form.Group className="mb-3">
        <Form.Label>Password</Form.Label>
        <InputGroup className="mb-3">
          <Form.Control
            type={showPw ? 'text' : 'password'}
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onBlur={() => setTouched((t) => ({ ...t, password: true }))}
            isValid={touched.password && passwordIsValid}
            isInvalid={touched.password && !passwordIsValid}
            autoComplete="current-password"
            required
          />

          <Form.Control.Feedback type="invalid">Please enter your password.</Form.Control.Feedback>

          <Button variant="outline-secondary" onClick={() => setShowPw((s) => !s)} tabIndex={-1}>
            {showPw ? 'Hide' : 'Show'}
          </Button>
        </InputGroup>
        <Form.Text className="text-muted">
          <NavLink to="/home/reset-password" className="text-reset text-decoration-none">
            Forgot password?
          </NavLink>
        </Form.Text>
      </Form.Group>

      <Button
        type="submit"
        variant="primary"
        disabled={!formIsValid || submitting}
        className="w-100"
      >
        {submitting && (
          <>
            <FontAwesomeIcon icon={faSpinner} spin />{' '}
          </>
        )}
        {submitLabel}
      </Button>
    </Form>
  );
};
